import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password"); // 'password' | 'mfa'
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const finish = (data) => {
    login(data.token, data.user);
    toast.success("Signed in as administrator");
    navigate("/admin");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      if (data.mfa_required) {
        setChallenge(data.challenge_token);
        setStep("mfa");
        toast.message("Enter the 6-digit code from your authenticator app");
      } else {
        finish(data);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/mfa/verify", { challenge_token: challenge, code });
      finish(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 shadow-2xl">
          <div className="flex items-center gap-2 text-primary mb-6">
            <ShieldCheck className="w-6 h-6" />
            <span className="font-num text-xs uppercase tracking-[0.2em] text-slate-500">Admin console</span>
          </div>
          {step === "password" ? (
            <>
              <h2 className="font-display text-2xl font-bold text-slate-900">Administrator sign in</h2>
              <p className="text-sm text-slate-500 mt-2 mb-6">Manage the approved viewer list and briefing content.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Email</label>
                  <Input
                    data-testid="admin-login-email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 h-11"
                    placeholder="admin@wedelvrlogistics.com.au"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Password</label>
                  <Input
                    data-testid="admin-login-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 h-11"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  data-testid="admin-login-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-primary hover:bg-primary/90"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold text-slate-900">Two-step verification</h2>
              <p className="text-sm text-slate-500 mt-2 mb-6">Open your authenticator app and enter the current 6-digit code for WE DELVR Briefing.</p>
              <form onSubmit={verifyMfa} className="space-y-4">
                <Input
                  data-testid="admin-mfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="mt-1.5 h-12 text-center tracking-[0.5em] text-xl font-num"
                  placeholder="000000"
                />
                <Button
                  data-testid="admin-mfa-verify"
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full h-11 bg-primary hover:bg-primary/90"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & sign in"}
                </Button>
                <button type="button" onClick={() => { setStep("password"); setCode(""); }} className="w-full text-xs text-slate-500 hover:text-primary">
                  Cancel
                </button>
              </form>
            </>
          )}
          <Link to="/login" className="flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-primary mt-6">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to viewer login
          </Link>
        </div>
      </div>
    </div>
  );
}
